import { useRef, useState, useCallback } from 'react';
import { Button, Modal } from '@safetyhub/ui';
import { SignaturePadWithRef } from '../SignaturePad';
import type { SignaturePadRef } from '../SignaturePad';
import { MODAL_LAYER } from '../modalLayers';

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

/**
 * 모바일 바닥 밀착 패널 — base `Modal`의 `panelClassName`으로 **바닥·좌우 가장자리에 붙고**
 * sm 이상에서는 종전처럼 가운데 카드로 돌아간다. `SignatureModal` bottom과 폰 점검완료 모달이 쓴다
 * (서명 패드는 폰에서 면적이 곧 기능이라 16px 뜬 카드로 줄이지 않는다).
 *
 * ⛔ base가 이미 붙이는 클래스와 **같은 property를 덧칠하지 않는다**(승자가 Tailwind 출력 순서에
 * 달린다 — base `overlayBgClassName` docblock과 같은 축). 그래서 property마다 우회한다:
 * - 아래 붙임: 오버레이 `items-center`(align-items) 대신 패널 `self-end`(align-self)
 * - 가장자리 밀착: 오버레이 `p-4`(padding)는 그대로 두고 패널 음수 마진 `-mx-4 -mb-4`(margin)
 * - 폭: 패널 `w-full`·`max-w-md`(width·max-width) 대신 `min-w-[calc(100%+2rem)]`(min-width) —
 *   CSS에서 min-width는 width·max-width를 **이긴다**(그래서 음수 마진만큼 넓어져 화면 폭을 채운다)
 * - 아래 모서리: `rounded-b-none`이 base `rounded-modal`과 같은 border-radius 계열이라 유일한 예외인데,
 *   2026-10-28 web CSS 산출에서 `rounded-b-none`이 `rounded-modal` **뒤**에 나온다(Tailwind
 *   borderRadius 플러그인이 전체 → 변별 → 모서리 순으로 낸다)를 확인하고 썼다.
 * ⚠️ 실제로 바닥에 붙어 보이나는 jsdom이 못 잰다 — 캡처가 소유한다.
 */
export const MOBILE_BOTTOM_PANEL_CLASS =
  'self-end -mx-4 -mb-4 min-w-[calc(100%+2rem)] rounded-b-none sm:self-center sm:mx-0 sm:mb-0 sm:min-w-0 sm:rounded-b-modal';

export interface SignatureModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** 모달 제목. 기본값: "점검완료 서명" */
  title?: string;
  /** 서명 완료 시 호출. signatureData는 base64 dataURL */
  onSubmit: (signatureData: string) => void | Promise<void>;
  /** 제출 중 상태 (버튼 비활성화 + 로딩 텍스트) */
  isSubmitting?: boolean;
  /** 에러 메시지 */
  error?: string;
  /**
   * 모달 패널 위치 방식.
   * - "center": 화면 중앙 (데스크톱/폴드 기본)
   * - "bottom": 모바일 바텀시트 스타일 (sm 이상에서는 center로 전환)
   */
  variant?: 'center' | 'bottom';
}

/**
 * 점검완료 서명 모달 — Desktop, Phone, Fold 레이아웃 공용
 *
 * @example
 * <SignatureModal
 *   isOpen={showSignatureModal}
 *   onClose={() => setShowSignatureModal(false)}
 *   onSubmit={async (data) => {
 *     await completion.handleCompleteMyInspection({ signatureData: data });
 *     setShowSignatureModal(false);
 *   }}
 *   isSubmitting={completion.isSubmitting}
 *   error={completion.error}
 * />
 */
export function SignatureModal({
  isOpen,
  onClose,
  title = '점검완료 서명',
  onSubmit,
  isSubmitting = false,
  error,
  variant = 'center',
}: SignatureModalProps) {
  const signaturePadRef = useRef<SignaturePadRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [hasSignature, setHasSignature] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);

  const handleImageLoad = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImageError(null);

    if (!file.type.startsWith('image/')) {
      setImageError('이미지 파일만 업로드할 수 있습니다');
      e.target.value = '';
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setImageError('파일 크기는 5MB 이하여야 합니다');
      e.target.value = '';
      return;
    }

    try {
      await signaturePadRef.current?.loadImage(file);
    } catch {
      setImageError('이미지를 불러오는 데 실패했습니다');
    } finally {
      e.target.value = '';
    }
  }, []);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    const signatureData = signaturePadRef.current?.toDataURL();
    if (!signatureData) return;
    try {
      await onSubmit(signatureData);
      setHasSignature(false);
      signaturePadRef.current?.clear();
    } catch {
      // 에러는 error prop을 통해 상위에서 관리
    }
  };

  const handleClose = () => {
    setHasSignature(false);
    setImageError(null);
    signaturePadRef.current?.clear();
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };

  const isBottom = variant === 'bottom';

  /*
    base Modal이 오버레이·Esc·✕·포커스 트랩·스크롤 잠금을 소유한다(종전 자체 Esc effect는 걷었다 —
    레지스트리를 몰라 위에 뜬 모달과 함께 닫혔다). 서명 제출 중에는 닫기 수단을 전부 막는다.

    `bottom`은 모바일에서 화면 바닥·좌우에 밀착하는 시트 모양이다(sm 이상은 가운데 카드) —
    방법과 그 제약은 `MOBILE_BOTTOM_PANEL_CLASS` docblock에 있다.
  */
  return (
    <Modal
      open
      onClose={handleClose}
      title={title}
      size="md"
      overlayZClassName={MODAL_LAYER.sub}
      panelClassName={isBottom ? MOBILE_BOTTOM_PANEL_CLASS : ''}
      closeLocked={isSubmitting}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isSubmitting}
            className={isBottom ? 'flex-1' : ''}
          >
            취소
          </Button>
          {/* '서명 완료'는 확정 어포던스라 초록(success) 유지 — 브랜드로 옮기지 않는다. */}
          <Button
            variant="success"
            disabled={!hasSignature || isSubmitting}
            onClick={handleSubmit}
            className={isBottom ? 'flex-1' : ''}
          >
            {isSubmitting ? '서명 중...' : '서명 완료'}
          </Button>
        </>
      }
    >
          <div className="space-y-4">
            {/* 서명 패드 */}
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1">서명</label>
              <SignaturePadWithRef
                ref={signaturePadRef}
                height={150}
                onSignatureChange={setHasSignature}
              />
            </div>

            {/* 이미지 불러오기 버튼 */}
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png"
                className="hidden"
                aria-label="서명 이미지 파일 선택"
                onChange={handleImageLoad}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-sm text-ink-600 hover:text-ink-900 underline"
              >
                이미지 불러오기
              </button>
              {imageError && <p className="mt-1 text-xs text-red-600">{imageError}</p>}
            </div>

            {/* 에러 메시지 */}
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
    </Modal>
  );
}
